import { createContext, useContext, useState, useEffect } from 'react'
import api from '../lib/api'

const AuthContext = createContext(null)

// DEV MODE: Set to true to bypass auth with mock user
const DEV_BYPASS_AUTH = false

const MOCK_USER = {
  id: 1,
  email: 'trader@alphalens.org',
  tier: 'pro',  // 'free', 'pro', or 'elite'
  daily_deliberations: 3,
  daily_limit: 100,
  created_at: '2025-01-01T00:00:00Z'
}

// Normalize user data from API (tier comes as PascalCase, convert to lowercase)
const normalizeUser = (userData) => {
  if (!userData) return null
  return {
    ...userData,
    tier: userData.tier?.toLowerCase() || 'free'
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(DEV_BYPASS_AUTH ? MOCK_USER : null)
  const [loading, setLoading] = useState(DEV_BYPASS_AUTH ? false : true)

  useEffect(() => {
    if (DEV_BYPASS_AUTH) return // Skip auth check in dev mode

    // Check for existing session on mount
    const token = api.getToken()
    if (token) {
      api.getMe()
        .then(data => setUser(normalizeUser(data.user || data)))
        .catch(() => {
          api.setToken(null)
          setUser(null)
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  const login = async (email, password) => {
    const data = await api.login(email, password)
    setUser(normalizeUser(data.user))
    return data
  }

  const register = async (email, password) => {
    const data = await api.register(email, password)
    setUser(normalizeUser(data.user))
    return data
  }

  const logout = async () => {
    await api.logout()
    setUser(null)
  }

  const refreshUser = async () => {
    const data = await api.getMe()
    const userData = normalizeUser(data.user || data)
    setUser(userData)
    return userData
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
